import { getSupabase } from '../auth/supabase.js'
import { deleteStore, readAllStore, readStore, writeStore } from '../storage/db.js'
import { mergeView, normalizeDocument, stripViewport } from './document.js'
import { localViewState, restoreLocalView, sameCamera } from './view-state.js'

function browserDeviceId() {
  const key = 'blaze.web.device-id'
  let value = localStorage.getItem(key)
  if (!value) { value = crypto.randomUUID(); localStorage.setItem(key, value) }
  return value
}

function createCanvasId() { return `cv_${crypto.randomUUID().replaceAll('-', '')}` }

export class CanvasRepository extends EventTarget {
  constructor(assetMap, options = {}) {
    super()
    this.assetMap = assetMap
    this.channels = new Map()
    this.supabase = options.supabase || getSupabase
    this.store = options.store || { delete: deleteStore, read: readStore, readAll: readAllStore, write: writeStore }
    this.deviceId = options.deviceId || browserDeviceId()
    this.viewSyncDelay = options.viewSyncDelay ?? 2_000
    this.viewTimers = new Map()
    this.assetMap.addEventListener?.('upload-status', event => {
      if (event.detail?.status === 'committed') void this.#flushReadyOutboxes().catch(() => {})
      if (event.detail?.status === 'failed' || event.detail?.status === 'cancelled') void this.#markUploadFailure(event.detail.hash).catch(() => {})
    })
  }

  async list() {
    const sb = this.supabase()
    if (sb) {
      const { data, error } = await sb.from('canvas_index').select('canvas_id,name,item_count,preview_hashes,created_at,updated_at').eq('deleted', false).order('updated_at', { ascending: false })
      if (!error) return (data || []).map(row => ({ id: row.canvas_id, name: row.name, itemCount: row.item_count, previews: row.preview_hashes || [], createdAt: row.created_at, updatedAt: row.updated_at }))
    }
    const local = await this.store.readAll('canvas_working_copies')
    return local.map(row => ({ id: row.id, name: row.document?.name || 'Untitled', itemCount: row.document?.items?.length || 0, previews: [], createdAt: row.document?.createdAt, updatedAt: row.updatedAt })).sort((a, b) => Number(b.updatedAt) - Number(a.updatedAt))
  }

  async remote(id) {
    const sb = this.supabase()
    if (!sb) return null
    const { data, error } = await sb.from('synced_records').select('entity_id,version,deleted,payload,updated_at').eq('kind', 'canvas').eq('entity_id', id).maybeSingle()
    if (error) throw error
    return data
  }

  async remoteView(id) {
    const sb = this.supabase()
    if (!sb?.from) return null
    const { data, error } = await sb.from('canvas_user_state')
      .select('canvas_id,center_x,center_y,zoom,device_id,updated_at')
      .eq('canvas_id', id)
      .maybeSingle()
    if (error) throw error
    if (!data) return null
    return {
      canvasId: data.canvas_id,
      centerX: Number(data.center_x),
      centerY: Number(data.center_y),
      zoom: Number(data.zoom),
      deviceId: data.device_id || null,
      cloudUpdatedAt: data.updated_at || null,
      syncStatus: 'synced',
    }
  }

  async load(id, viewportSize = null) {
    const [local, localView, outbox] = await Promise.all([
      this.store.read('canvas_working_copies', id), this.store.read('canvas_view_state', id), this.store.read('sync_outbox', id),
    ])
    const [remoteResult, viewResult] = await Promise.allSettled([this.remote(id), this.remoteView(id)])
    if (remoteResult.status === 'rejected' && !local) throw remoteResult.reason
    const remote = remoteResult.status === 'fulfilled' ? remoteResult.value : null
    const cloudView = viewResult.status === 'fulfilled' ? viewResult.value : null
    if (remote?.deleted) return null
    const useLocal = outbox?.status === 'pending' || outbox?.status === 'conflict'
    const document = normalizeDocument(id, useLocal && local ? local.document : remote?.payload || local?.document || { id, name: 'Untitled', items: [] })
    const version = Number(useLocal ? outbox?.baseVersion : remote?.version) || 0
    const selectedView = localView?.syncStatus === 'pending' ? localView : cloudView || localView
    const restoredView = restoreLocalView(selectedView, viewportSize)
    await this.assetMap.prime(document)
    const writes = [this.store.write('canvas_working_copies', { id, document, version, updatedAt: Date.now() })]
    if (selectedView) {
      writes.push(this.store.write('canvas_view_state', {
        ...selectedView, ...restoredView, canvasId: id,
        updatedAt: localView?.syncStatus === 'pending' ? localView.updatedAt : Date.now(),
      }))
    }
    await Promise.all(writes)
    if (localView?.syncStatus === 'pending') this.#scheduleViewPush(id)
    return { document: mergeView(document, restoredView), version, conflict: outbox?.status === 'conflict' }
  }

  async stageLocal(id, rawDocument, baseVersion, viewportSize = null) {
    const [current, pending, previousView] = await Promise.all([
      this.store.read('canvas_working_copies', id),
      this.store.read('sync_outbox', id),
      this.store.read('canvas_view_state', id),
    ])
    const document = normalizeDocument(id, stripViewport(rawDocument), current?.document)
    const nextView = localViewState(id, rawDocument, viewportSize, previousView)
    const viewChanged = !previousView || !sameCamera(previousView, nextView)
    const view = {
      ...nextView,
      syncStatus: viewChanged ? 'pending' : previousView.syncStatus,
      cloudUpdatedAt: previousView?.cloudUpdatedAt || null,
    }
    if (!pending && current?.version > 0 && JSON.stringify(current.document) === JSON.stringify(document)) {
      await this.store.write('canvas_view_state', view)
      if (view.syncStatus === 'pending') this.#scheduleViewPush(id)
      this.#status(id, 'local')
      return { ok: true, unchanged: true, document, version: current.version }
    }
    const conflict = pending?.status === 'conflict'
    const outbox = conflict
      ? { ...pending, document, updatedAt: Date.now() }
      : { canvasId: id, document, baseVersion, status: 'pending', attempts: pending?.attempts || 0, updatedAt: Date.now() }
    await Promise.all([
      this.store.write('canvas_working_copies', { id, document, version: baseVersion, updatedAt: Date.now() }),
      this.store.write('canvas_view_state', view),
      this.store.write('sync_outbox', outbox),
    ])
    if (view.syncStatus === 'pending') this.#scheduleViewPush(id)
    this.#status(id, conflict ? 'conflict' : 'local', { dirty: true })
    return { ok: !conflict, conflict, document, version: baseVersion }
  }

  async save(id, rawDocument, baseVersion, viewportSize = null) {
    const staged = await this.stageLocal(id, rawDocument, baseVersion, viewportSize)
    if (staged.conflict || staged.unchanged) return staged
    const assetState = this.assetMap.commitState
      ? await this.assetMap.commitState(staged.document)
      : (await this.assetMap.allCommitted(staged.document) ? 'committed' : 'uploading')
    if (assetState !== 'committed') {
      const status = assetState === 'failed' || assetState === 'cancelled' ? 'upload-error' : 'uploading'
      this.#status(id, status, { dirty: true })
      return { ok: true, localOnly: true, uploading: status === 'uploading', uploadError: status === 'upload-error', version: baseVersion }
    }
    return this.flush(id)
  }

  async flush(id) {
    const outbox = await this.store.read('sync_outbox', id)
    if (!outbox || outbox.status === 'conflict') return { ok: false, conflict: outbox?.status === 'conflict' }
    const sb = this.supabase()
    if (!sb) return { ok: true, localOnly: true, version: outbox.baseVersion }
    this.#status(id, 'syncing')
    const record = { kind: 'canvas', entity_id: id, parent_id: null, deleted: false, device_id: this.deviceId, payload: outbox.document, expected_prev_version: Number(outbox.baseVersion) || 0 }
    let data
    try {
      const response = await sb.rpc('sync_push', { p_records: [record] })
      if (response.error) throw response.error
      data = response.data
    } catch (error) {
      await this.store.write('sync_outbox', { ...outbox, attempts: Number(outbox.attempts || 0) + 1, lastError: String(error?.message || error), updatedAt: Date.now() })
      this.#status(id, 'local', { dirty: true })
      return { ok: true, localOnly: true, retryable: true, version: outbox.baseVersion }
    }
    const conflict = data?.conflicts?.find(item => item.kind === 'canvas' && item.entity_id === id)
    if (conflict) {
      await this.store.write('sync_outbox', { ...outbox, status: 'conflict', currentVersion: conflict.current_version, updatedAt: Date.now() })
      this.#status(id, 'conflict')
      return { ok: false, conflict: true, currentVersion: conflict.current_version }
    }
    const accepted = data?.accepted?.find(item => item.kind === 'canvas' && item.entity_id === id)
    const version = Number(accepted?.version) || outbox.baseVersion
    await Promise.all([
      this.store.delete('sync_outbox', id),
      this.store.write('canvas_working_copies', { id, document: outbox.document, version, updatedAt: Date.now() }),
    ])
    this.#status(id, 'cloud', { version, dirty: false })
    return { ok: true, version }
  }

  async flushView(id) {
    clearTimeout(this.viewTimers.get(id))
    this.viewTimers.delete(id)
    const candidate = await this.store.read('canvas_view_state', id)
    if (!candidate || candidate.syncStatus !== 'pending') return { ok: true, unchanged: true }
    const sb = this.supabase()
    if (!sb?.rpc) return { ok: true, localOnly: true }
    const response = await sb.rpc('save_canvas_user_state', {
      p_canvas_id: id,
      p_center_x: candidate.centerX,
      p_center_y: candidate.centerY,
      p_zoom: candidate.zoom,
      p_device_id: this.deviceId,
    })
    if (response.error) {
      const latest = await this.store.read('canvas_view_state', id)
      if (latest?.updatedAt === candidate.updatedAt) {
        await this.store.write('canvas_view_state', {
          ...latest, syncStatus: 'pending', lastError: String(response.error.message || response.error),
        })
      } else if (latest?.syncStatus === 'pending') this.#scheduleViewPush(id)
      return { ok: false, retryable: true }
    }
    const latest = await this.store.read('canvas_view_state', id)
    if (latest?.updatedAt !== candidate.updatedAt) {
      this.#scheduleViewPush(id)
      return { ok: true, superseded: true }
    }
    const row = Array.isArray(response.data) ? response.data[0] : response.data
    await this.store.write('canvas_view_state', {
      ...latest,
      syncStatus: 'synced',
      cloudUpdatedAt: row?.updated_at || new Date().toISOString(),
      lastError: undefined,
    })
    return { ok: true }
  }

  async create(name = 'Untitled') {
    const id = createCanvasId()
    const document = normalizeDocument(id, { id, name, items: [] })
    await this.store.write('canvas_working_copies', { id, document, version: 0, updatedAt: Date.now() })
    await this.save(id, document, 0)
    return id
  }

  async rename(id, name) {
    const loaded = await this.load(id)
    if (!loaded) throw new Error('canvas_not_found')
    return this.save(id, { ...loaded.document, name }, loaded.version)
  }

  async duplicate(id, source = null) {
    const loaded = source ? { document: source } : await this.load(id)
    if (!loaded) throw new Error('canvas_not_found')
    const nextId = createCanvasId()
    const document = normalizeDocument(nextId, { ...loaded.document, id: nextId, name: `${loaded.document.name} Copy`, createdAt: Date.now() })
    await this.save(nextId, document, 0)
    return nextId
  }

  async delete(id) {
    const remote = await this.remote(id)
    const sb = this.supabase()
    if (sb && remote) {
      const { error } = await sb.rpc('sync_push', { p_records: [{ kind: 'canvas', entity_id: id, parent_id: null, deleted: true, device_id: this.deviceId, payload: {}, expected_prev_version: remote.version }] })
      if (error) throw error
    }
    clearTimeout(this.viewTimers.get(id))
    this.viewTimers.delete(id)
    if (sb?.from) {
      try { await sb.from('canvas_user_state').delete().eq('canvas_id', id) } catch {}
    }
    await Promise.all([this.store.delete('canvas_working_copies', id), this.store.delete('canvas_view_state', id), this.store.delete('sync_outbox', id)])
  }

  async discardLocal(id) {
    await Promise.all([this.store.delete('sync_outbox', id), this.store.delete('canvas_working_copies', id)])
    return this.load(id)
  }

  subscribe(id, listener) {
    const sb = this.supabase()
    if (!sb) return () => {}
    const channel = sb.channel(`canvas:${id}:${crypto.randomUUID()}`).on('postgres_changes', { event: '*', schema: 'public', table: 'synced_records', filter: `entity_id=eq.${id}` }, payload => listener(payload.new || payload.old)).subscribe()
    this.channels.set(id, channel)
    return () => { this.channels.delete(id); void sb.removeChannel(channel) }
  }

  #scheduleViewPush(id) {
    const sb = this.supabase()
    if (!sb?.from || !sb?.rpc) return
    clearTimeout(this.viewTimers.get(id))
    this.viewTimers.set(id, setTimeout(() => {
      this.viewTimers.delete(id)
      void this.flushView(id).catch(() => {})
    }, this.viewSyncDelay))
  }

  async #flushReadyOutboxes() {
    const rows = await this.store.readAll('sync_outbox')
    for (const row of rows) {
      if (row.status !== 'pending' || !(await this.assetMap.allCommitted(row.document))) continue
      await this.flush(row.canvasId)
    }
  }

  async #markUploadFailure(hash) {
    const rows = await this.store.readAll('sync_outbox')
    for (const row of rows) {
      if (!JSON.stringify(row.document).includes(`asset://${hash}.`)) continue
      this.#status(row.canvasId, 'upload-error', { dirty: true, hash })
    }
  }

  #status(id, status, extra = {}) {
    this.dispatchEvent(new CustomEvent('status', { detail: { id, status, ...extra } }))
  }
}
